import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert, AppTile, Button, IconKakaoTalk, Input, Modal, Skeleton, StatusHint,
} from '@indipia/ui-core';
import { hasKakaoKey } from './kakaoSdk';

// 공용 [공유하기] 팝업 (FE-01) — 스케줄·리포트·사진첩이 props 만 바꿔 쓴다.
// 고객 링크 전용(결정 48): 수신자·공종 선택 없음. 링크는 서버가 내려주는 고정 링크이며
// 만료 문구도 서버가 준 값만 표시한다(프론트 날짜 계산 금지). 복사·카톡 공유는 이력·로그로 남기지 않는다(결정 31).
// 서버 호출 코드 없음 — 링크 조회는 fetchLink 주입.

export interface ShareLink {
  url: string;
  /** 서버가 내려준 만료 안내 문구. 없으면 표시하지 않는다 */
  expiresNotice?: string;
}

export interface ShareLinkPopupProps {
  open: boolean;
  onClose: () => void;
  /** 대상 현장 — 바뀌면 링크를 다시 조회한다 */
  siteId: number | string;
  /** 고객 고정 링크 조회(서버 API 는 소비처가 연결) */
  fetchLink: () => Promise<ShareLink>;
  /** 모달 제목. 기본 '공유하기' */
  title?: string;
  /** 링크 줄 아래 화면별 안내 */
  notice?: ReactNode;
  /** 앱 타일 위 미리보기 슬롯(예: 리포트 요약 카드) */
  preview?: ReactNode;
  /** 카카오톡 아이콘 클릭 시 호출 — 보통 `(url) => shareViaKakao({...})`. 미주입이거나 VITE_KAKAO_JS_KEY 가 없으면 아이콘 비활성 */
  onKakaoShare?: (url: string) => void | Promise<void>;
}

const COPIED_MS = 2000;
const KAKAO_PENDING_TEXT = '카카오톡 공유는 준비 중이에요. 링크를 복사해서 보내 주세요.';

export function ShareLinkPopup({
  open, onClose, siteId, fetchLink, title = '공유하기', notice, preview, onKakaoShare,
}: ShareLinkPopupProps) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [link, setLink] = useState<ShareLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // 호출부가 인라인 화살표를 넘겨도 조회가 반복되지 않도록 최신 함수만 ref 로 보관
  const fetchRef = useRef(fetchLink);
  fetchRef.current = fetchLink;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState('loading');
    setLink(null);
    setCopied(false);
    setActionError(null);
    fetchRef.current()
      .then((res) => { if (!cancelled) { setLink(res); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [open, siteId, reloadKey]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const kakaoReady = !!onKakaoShare && hasKakaoKey();

  const handleCopy = useCallback(async () => {
    if (!link) return;
    setActionError(null);
    let ok = false;
    try {
      await navigator.clipboard.writeText(link.url);
      ok = true;
    } catch {
      // 클립보드 API 를 쓸 수 없는 환경 — 입력 칸 선택 후 복사 명령으로 한 번 더 시도
      inputRef.current?.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
    } else {
      inputRef.current?.select();
      setActionError('복사하지 못했어요. 링크를 선택해서 직접 복사해 주세요.');
    }
  }, [link]);

  const handleKakao = useCallback(async () => {
    if (!link || !onKakaoShare) return;
    setActionError(null);
    try {
      await onKakaoShare(link.url);
    } catch {
      setActionError('카카오톡 공유를 열지 못했어요. 링크를 복사해서 붙여넣어 주세요.');
    }
  }, [link, onKakaoShare]);

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-4">
        {preview}

        {state === 'error' ? (
          <Alert
            kind="error"
            action={<Button size="sm" variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>다시 시도</Button>}
          >
            공유 링크를 불러오지 못했어요.
          </Alert>
        ) : (
          <>
            <div className="flex justify-center">
              <AppTile
                tone="kakao"
                icon={<IconKakaoTalk width={24} height={24} />}
                label="카카오톡"
                disabled={state !== 'ready' || !kakaoReady}
                onClick={handleKakao}
                title={kakaoReady ? undefined : KAKAO_PENDING_TEXT}
              />
            </div>
            {state === 'ready' && !kakaoReady && (
              <StatusHint tone="muted" className="justify-center">{KAKAO_PENDING_TEXT}</StatusHint>
            )}

            <div className="flex items-center gap-2">
              {state === 'loading' ? (
                <Skeleton height={48} className="flex-1" aria-busy="true" aria-label="링크를 불러오는 중" />
              ) : (
                <Input
                  ref={inputRef}
                  width="fill"
                  readOnly
                  aria-label="공유 링크"
                  value={link?.url ?? ''}
                  onFocus={(e) => e.currentTarget.select()}
                />
              )}
              <Button variant="primary" disabled={state !== 'ready'} onClick={handleCopy}>
                {copied ? '복사됨 ✓' : '복사'}
              </Button>
            </div>
            <span className="sr-only" role="status" aria-live="polite">{copied ? '링크를 복사했어요' : ''}</span>

            {state === 'ready' && link?.expiresNotice && (
              <StatusHint tone="muted">{link.expiresNotice}</StatusHint>
            )}
            {notice != null && <StatusHint tone="muted">{notice}</StatusHint>}
          </>
        )}

        {actionError && <Alert kind="warning">{actionError}</Alert>}
      </div>
    </Modal>
  );
}
